import React from 'react';
import {
  KeyboardTypeOptions,
  NativeSyntheticEvent,
  ReturnKeyTypeOptions,
  StyleProp,
  TextInput,
  TextInputContentSizeChangeEventData,
  TextInputFocusEventData,
  TextInputKeyPressEventData,
  TextInputProps,
  TextInputSelectionChangeEventData,
  TextStyle,
} from 'react-native';
import { MBTextStyles } from '@shared/components/MBText';
import { MBTypographyStyles } from '@shared/components/typography';
import { Colors } from '@shared/styles';
import { Styles } from './styles';

export interface MBTextInputProps {
  /*
        Value to populate the text input with
    */
  value?: string;
  defaultValue?: string;
  /*
        Callback when text changes
    */
  onChangeText?: (value: string) => void;

  /*
        Calback when done/enter is pressed
    */
  onSubmitEditing?: () => void;

  /*
        Callbacks for when user focuses, loses focus on text input
    */
  onFocus?: (event: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  onBlur?: (event: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  onKeyPress?: TextInputProps['onKeyPress'];

  onPaste?: (value: string) => void | boolean;

  /*
        If set, the default border will not be rendered. Used eg in
        search bar where we want to put a border around the whole bar.
    */
  noBorder?: boolean;

  /*
        If set, the text input won't have any padding. Default is false.
    */
  noPadding?: boolean;

  // If false, text is not editable. The default value is true.
  editable?: boolean;

  // If set, the text input will lose focus when submitted (eg enter pressed)
  blurOnSubmit?: boolean;
  // If set, all text will be selected when the text input gains focus.
  selectTextOnFocus?: boolean;
  // If set, the text input will be focused when appearing
  autoFocus?: boolean;
  autoCorrect?: boolean;
  autoCapitalize?: TextInputProps['autoCapitalize'];

  align?: 'left' | 'center' | 'right';
  size?: 'normal' | 'large' | 'xlarge'; // TODO(rosco): migrate to body1, body1bold, h3, h4
  bold?: boolean;
  inputMode?: 'none' | 'text' | 'email' | 'decimal' | 'numeric' | 'tel' | 'search' | 'url';
  textContentType?: TextInputProps['textContentType'];
  // https://medium.com/@aaronmgdr/3e3e7e06e933
  nativeID?: string;

  /*
        Should the browser do automatic spell checking and
        highlight misspelled words (red underline).
        Default is false.
    */
  spellCheck?: boolean;

  style?: StyleProp<TextStyle>;

  placeholder?: string;
  placeholderColor?: string;
  multiline?: boolean;
  returnKeyType?: ReturnKeyTypeOptions;
  keyboardType?: KeyboardTypeOptions;
  enablesReturnKeyAutomatically?: boolean;

  nativeOnKeyPress?(key: string): void;
  nativeOnContentSizeChange?(
    event: NativeSyntheticEvent<TextInputContentSizeChangeEventData>,
  ): void;
  nativeOnSelectionChange?(event: NativeSyntheticEvent<TextInputSelectionChangeEventData>): void;
  scrollEnabled?: boolean;
  maxLength?: number;
  testID?: string;
}

export class MBTextInput extends React.PureComponent<MBTextInputProps, { isFocused: boolean }> {
  constructor(props: MBTextInputProps) {
    super(props);
    this.state = {
      isFocused: false,
    };
  }
  textInput = React.createRef<TextInput>();

  // TODO probably unused since don't seem to work
  focus = () => this.textInput.current?.focus();
  blur = () => this.textInput.current?.blur();
  isFocused = () => this.textInput.current?.isFocused();
  // end TODO

  onKeyPress = (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
    this.props.nativeOnKeyPress?.(event.nativeEvent.key);
    this.props.onKeyPress?.(event);
  };

  onPaste = (event: ClipboardEvent) => {
    if (!!this.props.onPaste && event.clipboardData) {
      const pastedText = event.clipboardData.getData('text');
      const shouldNotPreventDefault = this.props.onPaste(pastedText);
      if (!shouldNotPreventDefault) {
        event.preventDefault();
      }
    }
  };

  onFocus() {
    this.setState({ isFocused: true });
  }

  onBlur() {
    this.setState({ isFocused: false });
  }

  render() {
    const {
      style,
      noBorder,
      noPadding,
      align,
      bold,
      nativeOnContentSizeChange,
      nativeOnSelectionChange,
      placeholderColor = Colors.v2.gray50,
      size = 'normal',
      spellCheck = false,
      autoCorrect,
      onBlur,
      onFocus,
      ...otherProps
    } = this.props;

    return (
      <TextInput
        rejectResponderTermination={false}
        ref={this.textInput}
        {...otherProps}
        // @ts-ignore
        onPaste={this.onPaste}
        onFocus={e => {
          onFocus?.(e);
          this.onFocus();
        }}
        onBlur={e => {
          onBlur?.(e);
          this.onBlur();
        }}
        spellCheck={spellCheck}
        autoCorrect={autoCorrect}
        style={[
          Styles.default,
          !noBorder && Styles.border,
          !noBorder && this.state.isFocused && Styles.focusedBorder,
          noPadding ? Styles.removeAndroidDefaultPadding : Styles.padding,
          !!align && { textAlign: align },

          size === 'normal' && MBTypographyStyles.body1,
          size === 'normal' && bold && MBTextStyles.bold,
          size === 'large' && MBTypographyStyles.h4,
          size === 'xlarge' && MBTypographyStyles.h3,

          // Strip the lineHeight, which causes alignment issues on React Native
          { lineHeight: undefined },

          style,
        ]}
        placeholderTextColor={placeholderColor}
        underlineColorAndroid="transparent"
        onContentSizeChange={nativeOnContentSizeChange}
        onSelectionChange={nativeOnSelectionChange}
        onKeyPress={this.onKeyPress}
        scrollEnabled={this.props.scrollEnabled ?? true}
      />
    );
  }
}
